import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const plans = [
  {
    name: 'Starter',
    tagline: 'Perfect for small businesses getting started',
    price: '799',
    period: '/mo',
    features: [
      { text: '2 social platforms managed', included: true },
      { text: '12 posts per month', included: true },
      { text: 'Basic content creation', included: true },
      { text: 'Monthly performance report', included: true },
      { text: 'Community management', included: true },
      { text: 'Hashtag strategy', included: true },
      { text: 'Video & Reels production', included: false },
      { text: 'Paid ad management', included: false },
      { text: 'Influencer partnerships', included: false },
    ],
    cta: 'Get Started',
    highlighted: false,
  },
  {
    name: 'Growth',
    tagline: 'For brands ready to scale their presence',
    price: '1,499',
    period: '/mo',
    features: [
      { text: '4 social platforms managed', included: true },
      { text: '24 posts per month', included: true },
      { text: 'Premium content creation', included: true },
      { text: 'Weekly performance reports', included: true },
      { text: 'Community management', included: true },
      { text: 'Hashtag & SEO strategy', included: true },
      { text: '4 Reels / TikToks per month', included: true },
      { text: 'Paid ad management (up to \u00a32k spend)', included: true },
      { text: 'Influencer partnerships', included: false },
    ],
    cta: 'Start Growing',
    highlighted: true,
  },
  {
    name: 'Enterprise',
    tagline: 'Full-service for ambitious brands',
    price: '2,999',
    period: '/mo',
    features: [
      { text: 'Unlimited platforms managed', included: true },
      { text: '60+ posts per month', included: true },
      { text: 'Premium content & copywriting', included: true },
      { text: 'Real-time analytics dashboard', included: true },
      { text: 'Dedicated account manager', included: true },
      { text: 'Advanced SEO & hashtag strategy', included: true },
      { text: '12 Reels / TikToks per month', included: true },
      { text: 'Paid ad management (unlimited)', included: true },
      { text: 'Influencer campaign management', included: true },
    ],
    cta: 'Contact Us',
    highlighted: false,
  },
];

export default function Pricing() {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const { ref, isVisible } = useScrollAnimation<HTMLElement>();

  return (
    <section
      ref={ref}
      className={`pt-28 pb-24 bg-slate-50 transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className={`text-center mb-6 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}>
          <span className="inline-block px-4 py-2 bg-[#00989e]/10 text-[#00989e] font-semibold rounded-full text-sm mb-6">
            Transparent Pricing
          </span>
          <h2
            className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Invest in Your Growth
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Flexible packages designed to match your ambitions. No hidden fees, no long-term contracts — just results.
          </p>
        </div>

        {/* Billing toggle */}
        <div className={`flex items-center justify-center gap-3 mb-16 transition-all duration-700 delay-100 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}>
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
              billingCycle === 'monthly'
                ? 'bg-[#00989e] text-white'
                : 'bg-white text-slate-600 border border-slate-200 hover:border-[#00989e]'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle('annual')}
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 ${
              billingCycle === 'annual'
                ? 'bg-[#00989e] text-white'
                : 'bg-white text-slate-600 border border-slate-200 hover:border-[#00989e]'
            }`}
          >
            Annual
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
              billingCycle === 'annual' ? 'bg-white/20 text-white' : 'bg-amber-100 text-amber-700'
            }`}>
              Save 20%
            </span>
          </button>
        </div>

        {/* Cards */}
        <div className="grid lg:grid-cols-3 gap-8 items-stretch">
          {plans.map((plan, index) => {
            const rawPrice = parseInt(plan.price.replace(',', ''), 10);
            const displayPrice = billingCycle === 'annual'
              ? Math.round(rawPrice * 0.8).toLocaleString()
              : plan.price;

            return (
              <div
                key={index}
                className={`relative rounded-2xl p-8 lg:p-10 flex flex-col transition-all duration-300 hover:-translate-y-1 ${
                  plan.highlighted
                    ? 'bg-slate-900 text-white shadow-2xl ring-2 ring-[#00989e] lg:scale-105'
                    : 'bg-white text-slate-900 border border-slate-200 hover:shadow-xl'
                } ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: isVisible ? `${200 + index * 100}ms` : '0ms', transitionDuration: '500ms' }}
              >
                {plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="px-5 py-1.5 bg-[#00989e] text-white text-xs font-bold rounded-full uppercase tracking-wider whitespace-nowrap">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="mb-8">
                  <h3
                    className="text-xl font-bold mb-1"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    {plan.name}
                  </h3>
                  <p className={`text-sm ${plan.highlighted ? 'text-gray-400' : 'text-gray-500'}`}>
                    {plan.tagline}
                  </p>
                </div>

                <div className="flex items-end gap-1 mb-8">
                  <span className="text-lg font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    £
                  </span>
                  <span
                    className="text-5xl font-bold leading-none"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    {displayPrice}
                  </span>
                  <span className={`text-sm mb-1 ${plan.highlighted ? 'text-gray-400' : 'text-gray-500'}`}>
                    {plan.period}
                  </span>
                </div>

                <div className={`h-px mb-8 ${plan.highlighted ? 'bg-white/10' : 'bg-slate-100'}`} />

                <ul className="space-y-4 mb-10 flex-grow">
                  {plan.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start gap-3">
                      <div
                        className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          feature.included
                            ? 'bg-[#00989e]/15'
                            : plan.highlighted
                            ? 'bg-white/5'
                            : 'bg-slate-100'
                        }`}
                      >
                        {feature.included ? (
                          <i className="ri-check-line text-xs text-[#00989e]"></i>
                        ) : (
                          <i className={`ri-close-line text-xs ${plan.highlighted ? 'text-gray-600' : 'text-gray-300'}`}></i>
                        )}
                      </div>
                      <span
                        className={`text-sm leading-snug ${
                          feature.included
                            ? plan.highlighted
                              ? 'text-gray-200'
                              : 'text-slate-700'
                            : plan.highlighted
                            ? 'text-gray-600'
                            : 'text-gray-400'
                        }`}
                      >
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>

                <a
                  href="#contact"
                  className={`block text-center py-4 rounded-lg font-semibold text-sm transition-all cursor-pointer whitespace-nowrap ${
                    plan.highlighted
                      ? 'bg-[#00989e] text-white hover:bg-[#007a7f]'
                      : 'border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white'
                  }`}
                >
                  {plan.cta}
                </a>
              </div>
            );
          })}
        </div>

        {/* Trust bar */}
        <div className={`mt-16 flex flex-wrap items-center justify-center gap-x-10 gap-y-4 text-sm text-gray-500 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`} style={{ transitionDelay: isVisible ? '600ms' : '0ms' }}>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-shield-check-line text-[#00989e]"></i>
            </div>
            No long-term contracts
          </div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-calendar-check-line text-[#00989e]"></i>
            </div>
            Cancel anytime
          </div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-customer-service-2-line text-[#00989e]"></i>
            </div>
            Dedicated support
          </div>
        </div>
      </div>
    </section>
  );
}
